import config from "../../config/config";
import { sessionAwareFetch } from "./sessionAwareFetch";

let stripePromise = null;

/** Loads Stripe.js on first use (add-payment-method / card confirm flows only). */
async function ensureStripePromise() {
    if (!stripePromise) {
        const { loadStripe } = await import('@stripe/stripe-js/pure');
        stripePromise = loadStripe(config.stripePublishableKey);
    }
    return stripePromise;
}

/**
 * Parses an error response body and throws a descriptive Error.
 * Handles both { error, message } and { error } shapes from the backend.
 */
async function throwApiError(response) {
    let body = {};
    try {
        body = await response.json();
    } catch { /* ignore parse failures */ }

    const displayMessage = body.message || body.error || `HTTP error! status: ${response.status}`;
    const err = new Error(displayMessage);
    err.status = response.status;
    if (body.error) err.code = body.error;
    throw err;
}

export const stripeService = {

    /** Returns the Stripe.js promise for use with Elements provider */
    getStripePromise() {
        return ensureStripePromise();
    },

    async createSetupIntent(paymentMethodType) {
        const response = await sessionAwareFetch(`${config.apimUrl}/stripe-api/stripe/setup-intent`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': config.apimKeyStripeService,
            },
            body: JSON.stringify({ paymentMethodType }),
            credentials: 'include',
        });
        if (!response.ok) await throwApiError(response);
        return await response.json();
    },

    async getPaymentMethods() {
        const response = await sessionAwareFetch(`${config.apimUrl}/stripe-api/stripe/payment-methods`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': config.apimKeyStripeService,
            },
            credentials: 'include',
        });
        if (!response.ok) await throwApiError(response);
        return await response.json();
    },

    async deletePaymentMethod(paymentMethodId) {
        const response = await sessionAwareFetch(`${config.apimUrl}/stripe-api/stripe/payment-methods/${paymentMethodId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': config.apimKeyStripeService,
            },
            credentials: 'include',
        });
        if (!response.ok) await throwApiError(response);
        return await response.json();
    },

    async setDefaultPaymentMethod(paymentMethodId) {
        const response = await sessionAwareFetch(`${config.apimUrl}/stripe-api/stripe/payment-methods/default`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': config.apimKeyStripeService,
            },
            body: JSON.stringify({ paymentMethodId }),
            credentials: 'include',
        });
        if (!response.ok) await throwApiError(response);
        return await response.json();
    },

    async getSubscriptionStatus() {
        const response = await sessionAwareFetch(`${config.apimUrl}/stripe-api/stripe/billing-status`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': config.apimKeyStripeService,
            },
            credentials: 'include',
        });
        if (!response.ok) await throwApiError(response);
        return await response.json();
    },

    async getInvoices() {
        const response = await sessionAwareFetch(`${config.apimUrl}/stripe-api/stripe/invoices`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': config.apimKeyStripeService,
            },
            credentials: 'include',
        });
        if (!response.ok) await throwApiError(response);
        return await response.json();
    },

    /**
     * Records that the authenticated user agreed to the subscription.
     * Called right after Stripe.js confirms the card setup directly with Stripe —
     * Stripe remains the source of truth, this just persists a timestamp/user reference.
     */
    async recordSubscriptionAgreement(subscriptionAgreedAt, subscriptionAgreedByUserId) {
        const response = await sessionAwareFetch(`${config.apimUrl}/stripe-api/stripe/subscription-agreement`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Ocp-Apim-Subscription-Key': config.apimKeyStripeService,
            },
            body: JSON.stringify({ subscriptionAgreedAt, subscriptionAgreedByUserId }),
            credentials: 'include',
        });
        if (!response.ok) await throwApiError(response);
        return await response.json();
    },

    /**
     * Card flow: Creates a SetupIntent, then confirms using a Stripe CardElement.
     * The cardElement is a reference from useElements() — Stripe handles card data securely.
     *
     * @param {object} cardElement - Stripe CardElement reference from useElements()
     * @param {object} billingDetails - { name, email, address }
     * @param {string} subscriptionAgreedByUserId - authenticated user's own account ID
     * @param {boolean} isInitialSetup - whether this is the customer's first payment method.
     *   The subscription agreement can only be recorded once per business, so subsequent
     *   cards (e.g. adding a 2nd card) should skip this call.
     */
    async confirmCardSetupIntent(cardElement, billingDetails = {}, subscriptionAgreedByUserId, isInitialSetup = true) {
        try {
            const setupIntentResponse = await this.createSetupIntent('card');
            const { clientSecret } = setupIntentResponse;

            if (!clientSecret) {
                throw new Error('No client secret received from backend');
            }

            const stripe = await ensureStripePromise();
            if (!stripe) {
                throw new Error('Failed to initialize Stripe');
            }

            const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: billingDetails,
                },
            });

            if (error) {
                throw new Error(error.message);
            }

            if (isInitialSetup) {
                try {
                    await this.recordSubscriptionAgreement(new Date().toISOString(), subscriptionAgreedByUserId);
                } catch (agreementError) {
                    // Stripe already has the payment method on file — don't fail the card save over this.
                    console.error('Error recording subscription agreement:', agreementError);
                }
            }

            return {
                success: true,
                setupIntent,
                paymentMethodId: setupIntent?.payment_method,
            };
        } catch (error) {
            console.error('Error in confirmCardSetupIntent:', error);
            return {
                success: false,
                error: error.message || 'Unknown error occurred',
            };
        }
    },

    /**
     * Bank account flow: Creates a SetupIntent, then confirms with raw bank details.
     * ACH doesn't require Stripe Elements — raw data is allowed.
     *
     * @param {object} bankDetails - { routing_number, account_number, account_type, account_holder_type }
     * @param {object} billingDetails - { name, email, address }
     */
    async confirmBankSetupIntent(bankDetails, billingDetails = {}) {
        try {
            const setupIntentResponse = await this.createSetupIntent('us_bank_account');
            const { clientSecret } = setupIntentResponse;

            if (!clientSecret) {
                throw new Error('No client secret received from backend');
            }

            const stripe = await ensureStripePromise();
            if (!stripe) {
                throw new Error('Failed to initialize Stripe');
            }

            const { error, setupIntent } = await stripe.confirmUsBankAccountSetup(clientSecret, {
                payment_method: {
                    us_bank_account: {
                        routing_number: bankDetails.routing_number,
                        account_number: bankDetails.account_number,
                        account_holder_type: bankDetails.account_holder_type || 'individual',
                        account_type: bankDetails.account_type,
                    },
                    billing_details: billingDetails,
                },
            });

            if (error) {
                throw new Error(error.message);
            }

            return {
                success: true,
                setupIntent,
                paymentMethodId: setupIntent?.payment_method,
            };
        } catch (error) {
            console.error('Error in confirmBankSetupIntent:', error);
            return {
                success: false,
                error: error.message || 'Unknown error occurred',
            };
        }
    },
};
